import { Extension, type Editor } from '@tiptap/core';
import { closeHistory } from '@tiptap/pm/history';
import { Plugin, PluginKey, type EditorState } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { toast } from '@ifixit/ui/components/toast';
import { _js } from '@ifixit/utils/localize';
import type { MediaLibraryItem } from 'Shared/MediaManager/types';
import { canHoldWikiImage } from './wiki_image';

type PlaceholderMeta =
   | { add: { id: object; pos: number; count: number } }
   | { recount: { id: object; count: number } }
   | { remove: object };

const imageUploadKey = new PluginKey<DecorationSet>('imageUpload');

declare module '@tiptap/core' {
   interface Storage {
      imageUpload: ImageUploadStorage;
   }
}

interface ImageUploadStorage {
   controller: AbortController;
   pending: { count: number };
   /**
    * Whether an image is on its way into the page. The wiki editor holds
    * Save while it is, since a save would go out without the image.
    */
   uploading: boolean;
}

/**
 * Pasting or dropping image files into the wiki editor uploads them to the
 * person's media library and puts them where they landed, as if picked from
 * the Media Manager. While they upload, a placeholder holds the spot. It's
 * a decoration rather than a node, so it never saves, never syncs to
 * session storage, and never becomes an undo step.
 */
export const ImageUpload = Extension.create<Record<string, never>, ImageUploadStorage>({
   name: 'imageUpload',

   // Uploads outlive the paste but not the editor. Not tied to the plugin
   // view: ProseMirror rebuilds every plugin view whenever a plugin is
   // registered, as the toolbar's menus do after the editor mounts.
   addStorage() {
      return { controller: new AbortController(), pending: { count: 0 }, uploading: false };
   },

   // Placeholders come and go without changing the doc, so nothing else tells
   // the page to re-check its Save button. OriginalState's check is what
   // re-renders it for an edit. It's debounced, and typing just before the
   // paste would hold this one back half a second, so it's flushed.
   onTransaction({ transaction }) {
      if (!transaction.getMeta(imageUploadKey)) {
         return;
      }
      const uploading = findPlaceholders(imageUploadKey.getState(this.editor.state)).length > 0;
      if (uploading !== this.storage.uploading) {
         this.storage.uploading = uploading;
         const checkModified = this.editor.storage.originalState?.checkModified as
            | (((editor: Editor) => void) & { flush?: () => void })
            | undefined;
         checkModified?.(this.editor);
         checkModified?.flush?.();
      }
   },

   // Switching to source mode closes the editor. An upload it cancels may
   // still finish into the library, but not into the page.
   onDestroy() {
      const { count } = this.storage.pending;
      if (count > 0) {
         toast.error(
            count === 1
               ? _js('An image was still uploading and was left out of the page.')
               : _js('%1 images were still uploading and were left out of the page.').replace(
                    '%1',
                    String(count)
                 )
         );
      }
      this.storage.controller.abort();
   },

   addProseMirrorPlugins() {
      const editor = this.editor;
      const { controller, pending } = this.storage;

      return [
         new Plugin<DecorationSet>({
            key: imageUploadKey,
            state: {
               init: () => DecorationSet.empty,
               apply(tr, placeholders) {
                  const mapped = placeholders.map(tr.mapping, tr.doc);
                  const meta = tr.getMeta(imageUploadKey) as PlaceholderMeta | undefined;
                  if (meta && 'add' in meta) {
                     const { id, pos, count } = meta.add;
                     return mapped.add(tr.doc, [placeholder(id, pos, count)]);
                  }
                  if (meta && 'recount' in meta) {
                     const { id, count } = meta.recount;
                     const [current] = findPlaceholders(mapped, id);
                     return current
                        ? mapped
                             .remove([current])
                             .add(tr.doc, [placeholder(id, current.from, count)])
                        : mapped;
                  }
                  if (meta && 'remove' in meta) {
                     return mapped.remove(findPlaceholders(mapped, meta.remove));
                  }
                  return mapped;
               },
            },
            props: {
               decorations: state => imageUploadKey.getState(state),
               handlePaste: (view, event) => {
                  const data = event.clipboardData;
                  // Copying from Word or Google Docs puts a picture of the
                  // selection next to its text. The text is what was meant.
                  if (!data?.files?.length || data.getData('text/plain')) {
                     return false;
                  }
                  const tr = view.state.tr.deleteSelection();
                  return startUploads(view, Array.from(data.files), tr.selection.from, tr);
               },
               handleDrop: (view, event, _slice, moved) => {
                  // Moving content within the editor carries no files.
                  if (moved || !event.dataTransfer?.files?.length) {
                     return false;
                  }
                  const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
                  if (pos === undefined) {
                     return false;
                  }
                  return startUploads(
                     view,
                     Array.from(event.dataTransfer.files),
                     pos,
                     view.state.tr
                  );
               },
            },
         }),
      ];

      function startUploads(
         view: EditorView,
         files: File[],
         pos: number,
         tr: EditorState['tr']
      ): boolean {
         const id = {};
         view.dispatch(tr.setMeta(imageUploadKey, { add: { id, pos, count: files.length } }));
         pending.count += files.length;
         void uploadAll(id, files);
         // Handled even if none turn out to be images: a file dropped on the
         // page opens in the tab.
         return true;
      }

      async function uploadAll(id: object, files: File[]) {
         let pipeline: typeof import('./image_upload_pipeline');
         try {
            pipeline = await import('./image_upload_pipeline');
         } catch {
            // A chunk that fails to load would otherwise leave the placeholder,
            // and the Save it holds, up until a reload.
            pending.count -= files.length;
            if (!editor.isDestroyed) {
               insertAtPlaceholder(id, []);
               toast.error(_js('The image could not be uploaded. Try pasting it again.'));
            }
            return;
         }
         const images = pipeline.acceptedImages(files);
         pending.count -= files.length - images.length;
         if (editor.isDestroyed) {
            return;
         }
         if (images.length === 0) {
            insertAtPlaceholder(id, []);
            return;
         }
         if (images.length < files.length) {
            editor.view.dispatch(
               editor.state.tr
                  .setMeta(imageUploadKey, { recount: { id, count: images.length } })
                  .setMeta('addToHistory', false)
            );
         }

         const results = await Promise.all(
            images.map(async file => {
               try {
                  return await pipeline.uploadImage(file, controller.signal);
               } finally {
                  pending.count--;
               }
            })
         );
         if (!editor.isDestroyed) {
            insertAtPlaceholder(
               id,
               results.filter(item => item !== null),
               pipeline.uploadedContent
            );
         }
      }

      function insertAtPlaceholder(
         id: object,
         items: MediaLibraryItem[],
         uploadedContent?: typeof import('./image_upload_pipeline')['uploadedContent']
      ) {
         const placeholder = findPlaceholders(imageUploadKey.getState(editor.state), id)[0];
         const removePlaceholder = editor.state.tr.setMeta(imageUploadKey, { remove: id });
         // Deleting the text around the placeholder deletes it too. The
         // images stay in the library for the picker.
         if (!placeholder || items.length === 0 || !uploadedContent) {
            editor.view.dispatch(removePlaceholder.setMeta('addToHistory', false));
            return;
         }

         const $pos = editor.state.doc.resolve(placeholder.from);
         const content = uploadedContent(items, canHoldWikiImage(editor.schema, $pos));
         editor
            .chain()
            .command(({ tr }) => {
               tr.setMeta(imageUploadKey, { remove: id });
               // Typing next to the placeholder within the last 500 ms
               // would otherwise share the images' undo step.
               closeHistory(tr);
               return true;
            })
            .insertContentAt(placeholder.from, content, { updateSelection: false })
            .run();
      }
   },
});

function findPlaceholders(placeholders: DecorationSet | undefined, id?: object): Decoration[] {
   return (
      placeholders?.find(undefined, undefined, spec => id === undefined || spec.id === id) ?? []
   );
}

// side -1 keeps text typed right after the paste after the images, and
// keeps the placeholder before it.
function placeholder(id: object, pos: number, count: number): Decoration {
   return Decoration.widget(pos, () => placeholderElement(count), { id, side: -1 });
}

function placeholderElement(count: number): HTMLElement {
   const element = document.createElement('span');
   element.className =
      'inline-block animate-pulse rounded bg-gray-100 px-2 py-1 text-sm text-gray-600';
   element.setAttribute('role', 'status');
   element.textContent =
      count === 1
         ? _js('Uploading image…')
         : _js('Uploading %1 images…').replace('%1', String(count));
   return element;
}
